import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import PartyAutocomplete from "@/components/shared/PartyAutocomplete";

const GAS_TYPES = ["Nitrogen", "Oxygen", "Argon", "LPG (Commercial)"];

export default function CylinderForm({ form, setField, parties, onSelectOrOther }) {
  const cylinders = form.cylinders || [{ cylinder_no: "", full_weight: "", empty_weight: "" }];

  const setCylinders = (updated) => setField("cylinders", updated);

  const addCylinder = () => {
    setCylinders([...cylinders, { _uid: crypto.randomUUID(), cylinder_no: "", full_weight: "", empty_weight: "" }]);
  };

  const removeCylinder = (idx) => {
    if (cylinders.length <= 1) return;
    setCylinders(cylinders.filter((_, i) => i !== idx));
  };

  const updateCylinder = (idx, key, val) => {
    const updated = cylinders.map((c, i) => (i === idx ? { ...c, [key]: val } : c));
    setCylinders(updated);
  };

  const calcGasWeight = (cyl) => {
    const full = parseFloat(cyl.full_weight) || 0;
    const empty = parseFloat(cyl.empty_weight) || 0;
    return Math.max(0, full - empty).toFixed(2);
  };

  const totalGasWeight = cylinders
    .reduce((sum, c) => sum + (parseFloat(calcGasWeight(c)) || 0), 0)
    .toFixed(2);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Gas / Cylinder Type *</Label>
          <Select value={form.gas_type || ""} onValueChange={(v) => setField("gas_type", v)}>
            <SelectTrigger className="h-11" data-testid="field-gas-type">
              <SelectValue placeholder="Select gas type" />
            </SelectTrigger>
            <SelectContent>
              {GAS_TYPES.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Movement Type *</Label>
          <Select value={form.movement_type || "RGP"} onValueChange={(v) => setField("movement_type", v)}>
            <SelectTrigger className="h-11" data-testid="field-movement-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="RGP">RGP (Returnable)</SelectItem>
              <SelectItem value="NRGP">NRGP (Non-Returnable)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <PartyAutocomplete
            value={form.party_name || ""}
            onChange={(v) => setField("party_name", v)}
            label="Party / Supplier *"
            masterType="parties"
            testId="field-cyl-party-autocomplete"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No.</Label>
          <Input value={form.vehicle_no || ""} onChange={(e) => setField("vehicle_no", e.target.value)} className="h-11" data-testid="field-cyl-vehicle" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Challan / Invoice No.</Label>
          <Input value={form.challan_no || ""} onChange={(e) => setField("challan_no", e.target.value)} className="h-11" data-testid="field-cyl-challan" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Approved By</Label>
          <Input value={form.approved_by || ""} onChange={(e) => setField("approved_by", e.target.value)} className="h-11" />
        </div>
      </div>

      {/* Cylinder rows */}
      <div className="border rounded-lg p-3 bg-slate-50/50 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-600">
            Cylinders ({cylinders.length})
          </Label>
          <Button type="button" size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={addCylinder} data-testid="add-cylinder-btn">
            <Plus size={12} /> Add Cylinder
          </Button>
        </div>
        {cylinders.map((cyl, idx) => (
          <div key={cyl._uid || `cyl-${idx}`} className="grid grid-cols-[1fr_1fr_1fr_auto_auto] gap-2 items-end bg-white p-2 rounded border" data-testid={`cylinder-row-${idx}`}>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500">Full Wt (Kg)</Label>
              <Input
                type="number"
                step="0.01"
                value={cyl.full_weight}
                onChange={(e) => updateCylinder(idx, "full_weight", e.target.value)}
                className="h-9 text-sm"
                placeholder="0.00"
                data-testid={`cyl-full-wt-${idx}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500">Empty Wt (Kg)</Label>
              <Input
                type="number"
                step="0.01"
                value={cyl.empty_weight}
                onChange={(e) => updateCylinder(idx, "empty_weight", e.target.value)}
                className="h-9 text-sm"
                placeholder="0.00"
                data-testid={`cyl-empty-wt-${idx}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500">Gas Wt (Kg)</Label>
              <div className="h-9 flex items-center px-2 bg-emerald-50 border border-emerald-200 rounded text-sm font-semibold text-emerald-700" data-testid={`cyl-gas-wt-${idx}`}>
                {calcGasWeight(cyl)}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-500">Cyl No.</Label>
              <Input
                value={cyl.cylinder_no}
                onChange={(e) => updateCylinder(idx, "cylinder_no", e.target.value)}
                className="h-9 text-sm w-20"
                placeholder="#"
                data-testid={`cyl-no-${idx}`}
              />
            </div>
            <div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-9 w-9 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                onClick={() => removeCylinder(idx)}
                disabled={cylinders.length <= 1}
                data-testid={`remove-cyl-${idx}`}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-end gap-3 pt-1 border-t">
          <span className="text-xs text-gray-500 uppercase tracking-wider font-bold">Total Gas Weight:</span>
          <span className="text-sm font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded" data-testid="total-gas-weight">{totalGasWeight} Kg</span>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
        <Input value={form.remarks || ""} onChange={(e) => setField("remarks", e.target.value)} className="h-11" data-testid="field-cyl-remarks" />
      </div>
    </div>
  );
}
